<template>
  <div class="mega-product-detail bg-gray-50 min-h-screen pt-32 pb-20">
    <!-- Back Button -->
    <div class="container mx-auto px-4 mb-6">
      <RouterLink 
        to="/allProduct" 
        class="inline-flex items-center text-green-600 hover:text-green-800 transition-colors duration-300"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        {{ $t('productsSection.backToProducts') }}
      </RouterLink>
    </div>

    <!-- Main Product Content -->
    <div v-if="product" class="container mx-auto px-4">
      <div class="mega-product-container bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-500 hover:shadow-2xl">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 p-8">

          <!-- Product Images Gallery -->
          <div class="mega-product-gallery">
            <div class="relative overflow-hidden rounded-xl h-96 bg-gray-100 flex items-center justify-center">
              <img 
                :src="buildStorageUrl(product.img)"
                :alt="product.name"
                class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
              >
              <div class="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent"></div>
            </div>
            
            <div v-if="product.images && product.images.length > 0" class="grid grid-cols-4 gap-3 mt-4">
              <div 
                v-for="(image, index) in product.images" 
                :key="index"
                class="h-24 bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-green-500 transition-all duration-300"
                @click="currentImage = image"
              >
                <img 
                  :src="buildStorageUrl(image)"
                  :alt="`${product.name} - ${index + 1}`"
                  class="w-full h-full object-cover"
                >
              </div>
            </div>
          </div>

          <!-- Product Details -->
          <div class="mega-product-info">
            <h1 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{{ product.name }}</h1>
            <p class="text-gray-600 text-lg mb-6">{{ product.description }}</p>

            <!-- Documents Section -->
            <div class="mega-documents">
              <h3 class="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {{ $t('productsSection.documents') }}
              </h3>
              
              <div class="flex flex-wrap gap-4">
                <button 
                  v-if="product.documents && product.documents.length > 0" 
                  @click="openModal('view')" 
                  class="mega-doc-button bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {{ $t('productsSection.viewManual') }}
                </button>
                
                <a 
                  v-if="product.documents && product.documents.length > 0" 
                  :href="buildStorageUrl(product.documents[0].doc)"
                  download
                  target="_blank"
                  class="mega-doc-button bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {{ $t('productsSection.downloadManual') }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-else-if="loading" class="container mx-auto px-4 text-center py-20">
      <div class="mega-spinner mx-auto">
        <div class="mega-spinner-dot bg-green-600"></div>
        <div class="mega-spinner-dot bg-green-500"></div>
        <div class="mega-spinner-dot bg-green-400"></div>
        <div class="mega-spinner-dot bg-green-300"></div>
      </div>
      <p class="mt-6 text-gray-600">{{ $t('productsSection.loading') }}</p>
    </div>
    
    <!-- Error State -->
    <div v-else class="container mx-auto px-4 text-center py-20">
      <div class="inline-flex items-center bg-red-100/80 text-red-700 px-6 py-4 rounded-lg max-w-md mx-auto">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span class="text-lg font-medium">{{ $t('productsSection.notFound') }}</span>
      </div>
    </div>

    <!-- Modal for Viewing Manual -->
    <div 
      v-if="showModal" 
      class="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 mega-modal-overlay"
      @click.self="closeModal"
    >
      <div class="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col mega-modal-content">
        <div class="flex justify-between items-center bg-gray-100 px-6 py-4 border-b">
          <h3 class="text-xl font-semibold text-gray-800">{{ modalTitle }}</h3>
          <button 
            @click="closeModal" 
            class="text-gray-500 hover:text-gray-700 transition-colors duration-200 text-2xl"
          >
            &times;
          </button>
        </div>
        <div class="flex-1 overflow-auto">
          <iframe 
            v-if="viewManual && product.documents && product.documents.length > 0" 
            :src="`${buildStorageUrl(product.documents[0].doc)}#toolbar=0`"
            title="Product manual preview"
            class="w-full h-full min-h-[70vh] border-0"
          ></iframe>
        </div>
        <div class="bg-gray-100 px-6 py-3 border-t flex justify-end">
          <button 
            @click="closeModal" 
            class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300"
          >
            {{ $t('productsSection.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiService } from '@/services/apiService'
import { buildStorageUrl } from '@/apiConfig'

const { t } = useI18n()
const route = useRoute()
const showModal = ref(false)
const modalTitle = ref('')
const viewManual = ref(false)
const currentImage = ref(null)

const product = ref(null)
const loading = ref(true)
const error = ref(null)

const fetchProduct = async () => {
  loading.value = true
  error.value = null
  try {
    const res = await apiService.getProductById(route.params.id)
    product.value = res.data // ✅ your API returns the product directly
  } catch (err) {
    error.value = err.message || 'Failed to load product.'
  } finally {
    loading.value = false
  }
}

const openModal = (type) => {
  if (!product.value) return
  showModal.value = true
  if (type === 'view') {
    modalTitle.value = t('productsSection.viewManual')
    viewManual.value = true
  }
}

const closeModal = () => {
  showModal.value = false
  viewManual.value = false
}

onMounted(fetchProduct)
</script>









<style scoped>
.mega-product-detail {
  background-image: radial-gradient(circle at 10% 20%, rgba(0, 119, 255, 0.05) 0%, transparent 20%);
}

.mega-product-container {
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(5px);
}

.mega-product-gallery {
  position: relative;
}

.mega-product-info {
  position: relative;
}

.mega-specs {
  border: 1px solid rgba(0, 119, 255, 0.1);
}

.mega-doc-button {
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.mega-doc-button:hover {
  transform: translateY(-2px);
}

/* Modal Animations */
.mega-modal-overlay {
  animation: mega-fadeIn 0.3s ease-out;
}

.mega-modal-content {
  animation: mega-slideIn 0.3s ease-out;
}

@keyframes mega-fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes mega-slideIn {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Spinner Animation */
.mega-spinner {
  --uib-size: 60px;
  --uib-speed: 1.4s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--uib-size);
  height: var(--uib-size);
}

.mega-spinner-dot {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.mega-spinner-dot::before {
  content: '';
  display: block;
  width: 20%;
  height: 20%;
  border-radius: 50%;
  animation: mega-spin var(--uib-speed) linear infinite;
}

.mega-spinner-dot:nth-child(1)::before {
  animation-delay: calc(var(--uib-speed) * -0.45);
}

.mega-spinner-dot:nth-child(2)::before {
  animation-delay: calc(var(--uib-speed) * -0.3);
}

.mega-spinner-dot:nth-child(3)::before {
  animation-delay: calc(var(--uib-speed) * -0.15);
}

@keyframes mega-spin {
  0% {
    transform: scale(0);
    opacity: 0.5;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .mega-product-container {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .mega-product-detail {
    padding-top: 6rem;
    padding-bottom: 4rem;
  }

  .mega-product-info h1 {
    font-size: 2rem;
  }
}

@media (max-width: 640px) {
  .mega-documents .flex {
    flex-direction: column;
  }

  .mega-doc-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
